import React, { useContext, useEffect, useState } from "react";
import {
  Stack,
  Select,
  SelectItem,
  TextInput,
  Button,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Loading,
  InlineNotification,
} from "@carbon/react";
import { Add, Checkmark, Edit, TrashCan } from "@carbon/icons-react";
import { FormattedMessage, useIntl } from "react-intl";
import {
  getFromOpenElisServer,
  putToOpenElisServer,
} from "../../../utils/Utils";
import { NotificationContext } from "../../../layout/Layout";

/**
 * OGC-949 M10 / OGC-957..958 — Terminology Mappings section.
 *
 * Lists a test's terminology mappings (Source / Code / Relationship). Rows are
 * read-only until the admin clicks Edit; a row's fields are then editable in
 * place. New rows are added via the bottom form (either "Add mapping" or just
 * filling it and hitting Save). The whole set persists with PUT
 * /rest/test-catalog/tests/{id}/terminology. Source ∈ LOINC / SNOMED / CIEL /
 * OCL; relationship ∈ SAME_AS / BROADER_THAN / NARROWER_THAN.
 */
const SOURCES = ["LOINC", "SNOMED", "CIEL", "OCL"];
const RELATIONSHIPS = ["SAME_AS", "BROADER_THAN", "NARROWER_THAN"];
const SOURCE_TAG = {
  LOINC: "blue",
  SNOMED: "teal",
  CIEL: "purple",
  OCL: "cyan",
};

const TerminologySection = ({ testId }) => {
  const intl = useIntl();
  const { addNotification, setNotificationVisible } =
    useContext(NotificationContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mappings, setMappings] = useState([]);
  // The test's result components (id, code, label), so a mapping can be scoped
  // to a single component ("Applies to") instead of the whole test.
  const [components, setComponents] = useState([]);
  const [loincIntegrity, setLoincIntegrity] = useState(null);
  const [draft, setDraft] = useState({
    source: "",
    code: "",
    relationship: "",
    componentId: "",
  });

  const componentLabel = (id) => {
    const c = components.find((x) => x.id === id);
    if (!c) {
      return "";
    }
    return c.label || c.code || "";
  };
  // Row indices currently in edit mode; a row's fields are editable only after
  // its Edit button is clicked.
  const [editingRows, setEditingRows] = useState(() => new Set());

  const toggleEdit = (index) =>
    setEditingRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });

  const loadLoincIntegrity = () => {
    getFromOpenElisServer(
      `/rest/test-catalog/tests/${testId}/loinc-integrity`,
      (res) => setLoincIntegrity(res || null),
    );
  };

  const loadMappings = () => {
    getFromOpenElisServer(
      `/rest/test-catalog/tests/${testId}/terminology`,
      (res) => {
        setLoading(false);
        if (!res) {
          setError(true);
          return;
        }
        setMappings(res.mappings || []);
        setComponents(res.components || []);
      },
    );
  };

  useEffect(() => {
    if (!testId) {
      return;
    }
    setLoading(true);
    setError(false);
    loadMappings();
    loadLoincIntegrity();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testId]);

  // Edit an existing (or newly added) row in place.
  const updateMapping = (index, patch) =>
    setMappings((prev) =>
      prev.map((m, i) => (i === index ? { ...m, ...patch } : m)),
    );

  const addMapping = () => {
    if (!draft.source || !draft.code) {
      return;
    }
    setMappings((prev) => [
      ...prev,
      {
        id: null,
        source: draft.source,
        code: draft.code,
        relationship: draft.relationship || null,
        componentId: draft.componentId || null,
      },
    ]);
    setDraft({ source: "", code: "", relationship: "", componentId: "" });
  };

  const removeMapping = (index) => {
    setMappings((prev) => prev.filter((_, i) => i !== index));
    // Row indices shift after a removal; clear edit state to avoid the wrong row
    // appearing editable.
    setEditingRows(new Set());
  };

  const handleSave = () => {
    // Fold in a filled-but-not-yet-added draft so the admin can just type a row
    // and hit Save without first clicking "Add mapping".
    const all = [...mappings];
    if (draft.source && draft.code) {
      all.push({
        id: null,
        source: draft.source,
        code: draft.code,
        relationship: draft.relationship || null,
        componentId: draft.componentId || null,
      });
    }
    // Persist only complete rows (source + code); drop blank/partial ones.
    const complete = all.filter((m) => m.source && m.code);

    setSaving(true);
    const payload = {
      testId,
      mappings: complete.map((m) => ({
        id: m.id || null,
        source: m.source,
        code: m.code,
        relationship: m.relationship || null,
        componentId: m.componentId || null,
      })),
    };
    putToOpenElisServer(
      `/rest/test-catalog/tests/${testId}/terminology`,
      JSON.stringify(payload),
      (status) => {
        setSaving(false);
        setNotificationVisible(true);
        if (status === 200) {
          addNotification({
            kind: "success",
            title: intl.formatMessage({
              id: "label.testCatalog.section.terminology",
            }),
            message: intl.formatMessage({
              id: "label.testCatalog.terminology.saved",
            }),
          });
          // Refresh with server-assigned ids + recomputed LOINC integrity so the
          // next edit updates in place rather than inserting duplicates.
          setDraft({ source: "", code: "", relationship: "", componentId: "" });
          setEditingRows(new Set());
          loadMappings();
          loadLoincIntegrity();
        } else {
          addNotification({
            kind: "error",
            title: intl.formatMessage({ id: "error.title" }),
            message: intl.formatMessage({ id: "server.error.msg" }),
          });
        }
      },
    );
  };

  if (loading) {
    return (
      <Loading
        description={intl.formatMessage({ id: "label.loading" })}
        withOverlay={false}
      />
    );
  }
  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        hideCloseButton
        title={intl.formatMessage({ id: "error.title" })}
        subtitle={intl.formatMessage({
          id: "label.testCatalog.terminology.loadError",
        })}
      />
    );
  }

  return (
    <Stack gap={6} data-testid="terminology-section">
      <p>
        <FormattedMessage id="label.testCatalog.terminology.intro" />
      </p>

      {loincIntegrity && loincIntegrity.noLoinc && (
        <InlineNotification
          kind="warning"
          lowContrast
          hideCloseButton
          data-testid="no-loinc-warning"
          title={intl.formatMessage({ id: "warning.testCatalog.noLoinc" })}
        />
      )}
      {loincIntegrity &&
        loincIntegrity.duplicates &&
        loincIntegrity.duplicates.length > 0 && (
          <InlineNotification
            kind="warning"
            lowContrast
            hideCloseButton
            data-testid="duplicate-loinc-warning"
            title={intl.formatMessage(
              { id: "warning.testCatalog.duplicateLoinc" },
              {
                code: loincIntegrity.loinc,
                testName: loincIntegrity.duplicates
                  .map((d) => d.name)
                  .join(", "),
              },
            )}
          />
        )}

      {mappings.length === 0 ? (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title={intl.formatMessage({
            id: "label.testCatalog.terminology.empty",
          })}
        />
      ) : (
        <Table size="lg" aria-label="terminology">
          <TableHead>
            <TableRow>
              <TableHeader>
                <FormattedMessage id="label.testCatalog.terminology.col.source" />
              </TableHeader>
              <TableHeader>
                <FormattedMessage id="label.testCatalog.terminology.col.code" />
              </TableHeader>
              <TableHeader>
                <FormattedMessage id="label.testCatalog.terminology.col.relationship" />
              </TableHeader>
              <TableHeader>
                <FormattedMessage id="label.testCatalog.terminology.col.appliesTo" />
              </TableHeader>
              <TableHeader>
                <FormattedMessage id="label.testCatalog.terminology.col.actions" />
              </TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {mappings.map((m, i) => {
              const editing = editingRows.has(i);
              return (
                <TableRow
                  key={m.id || `new-${i}`}
                  data-testid={`mapping-row-${m.id || i}`}
                >
                  <TableCell>
                    {editing ? (
                      <Select
                        id={`mapping-source-${i}`}
                        labelText=""
                        value={m.source || ""}
                        onChange={(e) =>
                          updateMapping(i, { source: e.target.value })
                        }
                      >
                        <SelectItem value="" text="" />
                        {SOURCES.map((s) => (
                          <SelectItem key={s} value={s} text={s} />
                        ))}
                      </Select>
                    ) : (
                      <Tag type={SOURCE_TAG[m.source] || "gray"}>
                        {m.source}
                      </Tag>
                    )}
                  </TableCell>
                  <TableCell>
                    {editing ? (
                      <TextInput
                        id={`mapping-code-${i}`}
                        labelText=""
                        value={m.code || ""}
                        onChange={(e) =>
                          updateMapping(i, { code: e.target.value })
                        }
                      />
                    ) : (
                      <code>{m.code}</code>
                    )}
                  </TableCell>
                  <TableCell>
                    {editing ? (
                      <Select
                        id={`mapping-rel-${i}`}
                        labelText=""
                        value={m.relationship || ""}
                        onChange={(e) =>
                          updateMapping(i, {
                            relationship: e.target.value || null,
                          })
                        }
                      >
                        <SelectItem
                          value=""
                          text={intl.formatMessage({
                            id: "label.testCatalog.terminology.rel.none",
                          })}
                        />
                        {RELATIONSHIPS.map((r) => (
                          <SelectItem
                            key={r}
                            value={r}
                            text={intl.formatMessage({
                              id: `label.testCatalog.terminology.rel.${r}`,
                            })}
                          />
                        ))}
                      </Select>
                    ) : m.relationship ? (
                      <FormattedMessage
                        id={`label.testCatalog.terminology.rel.${m.relationship}`}
                      />
                    ) : (
                      ""
                    )}
                  </TableCell>
                  <TableCell>
                    {editing ? (
                      <Select
                        id={`mapping-component-${i}`}
                        labelText=""
                        value={m.componentId || ""}
                        onChange={(e) =>
                          updateMapping(i, {
                            componentId: e.target.value || null,
                          })
                        }
                      >
                        <SelectItem
                          value=""
                          text={intl.formatMessage({
                            id: "label.testCatalog.terminology.appliesTo.test",
                          })}
                        />
                        {components.map((c) => (
                          <SelectItem
                            key={c.id}
                            value={c.id}
                            text={c.label || c.code}
                          />
                        ))}
                      </Select>
                    ) : m.componentId ? (
                      <Tag type="green">{componentLabel(m.componentId)}</Tag>
                    ) : (
                      <FormattedMessage id="label.testCatalog.terminology.appliesTo.test" />
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={editing ? Checkmark : Edit}
                      data-testid={`edit-mapping-${i}`}
                      iconDescription={intl.formatMessage({
                        id: editing
                          ? "label.button.close"
                          : "label.button.edit",
                      })}
                      onClick={() => toggleEdit(i)}
                    />
                    <Button
                      kind="ghost"
                      size="sm"
                      hasIconOnly
                      renderIcon={TrashCan}
                      iconDescription={intl.formatMessage({
                        id: "label.testCatalog.terminology.remove",
                      })}
                      onClick={() => removeMapping(i)}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Stack gap={4} orientation="horizontal">
        <Select
          id="terminology-source"
          labelText={intl.formatMessage({
            id: "label.testCatalog.terminology.source",
          })}
          value={draft.source}
          onChange={(e) => setDraft({ ...draft, source: e.target.value })}
        >
          <SelectItem value="" text="" />
          {SOURCES.map((s) => (
            <SelectItem key={s} value={s} text={s} />
          ))}
        </Select>
        <TextInput
          id="terminology-code"
          labelText={intl.formatMessage({
            id: "label.testCatalog.terminology.code",
          })}
          value={draft.code}
          onChange={(e) => setDraft({ ...draft, code: e.target.value })}
        />
        <Select
          id="terminology-relationship"
          labelText={intl.formatMessage({
            id: "label.testCatalog.terminology.relationship",
          })}
          value={draft.relationship}
          onChange={(e) => setDraft({ ...draft, relationship: e.target.value })}
        >
          <SelectItem
            value=""
            text={intl.formatMessage({
              id: "label.testCatalog.terminology.rel.none",
            })}
          />
          {RELATIONSHIPS.map((r) => (
            <SelectItem
              key={r}
              value={r}
              text={intl.formatMessage({
                id: `label.testCatalog.terminology.rel.${r}`,
              })}
            />
          ))}
        </Select>
        <Select
          id="terminology-component"
          labelText={intl.formatMessage({
            id: "label.testCatalog.terminology.col.appliesTo",
          })}
          value={draft.componentId}
          onChange={(e) => setDraft({ ...draft, componentId: e.target.value })}
        >
          <SelectItem
            value=""
            text={intl.formatMessage({
              id: "label.testCatalog.terminology.appliesTo.test",
            })}
          />
          {components.map((c) => (
            <SelectItem key={c.id} value={c.id} text={c.label || c.code} />
          ))}
        </Select>
        <Button kind="tertiary" renderIcon={Add} onClick={addMapping}>
          <FormattedMessage id="label.testCatalog.terminology.addMapping" />
        </Button>
      </Stack>

      <Button kind="primary" disabled={saving} onClick={handleSave}>
        <FormattedMessage id="label.button.save" />
      </Button>
    </Stack>
  );
};

export default TerminologySection;
